'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

// ── Types ─────────────────────────────────────────────────────────────────────
interface CollaborativeAnnotation {
  id: string;
  ayatRef: string;
  ayatTitle: string;
  vantagePoint: string;
  observation: string;
  zahirNote: string;
  batinNote: string;
  authorRole: string;
  authorName: string;
  city: string;
  nameOfAllah: string;
  createdAt: string;
  reactions: { alhamdulilah: number; subhanallah: number; inspiring: number };
  conflictFlag?: boolean;
}

interface BatinDepthLayer {
  level: number;
  label: string;
  ar: string;
  description: string;
  unlockCondition: string;
  isUnlocked: boolean;
  color: string;
}

// ── Skeleton Loader ───────────────────────────────────────────────────────────
function SkeletonBlock({ height = 16, width = '100%', className = '' }: { height?: number; width?: string | number; className?: string }) {
  return (
    <div
      className={`rounded animate-pulse ${className}`}
      style={{ height, width, background: 'rgba(45,138,135,0.08)' }}
    />
  );
}

// ── Batin Depth Unlock System ─────────────────────────────────────────────────
const BATIN_DEPTH_LAYERS: BatinDepthLayer[] = [
  {
    level: 1,
    label: 'ZAHIR OBSERVATION',
    ar: 'ظَاهِر',
    description: 'The visible surface — what is immediately observable. The leaf, the ward, the community. Layer 1 of the IRL Lens: Encounter.',
    unlockCondition: 'Always visible',
    isUnlocked: true,
    color: '#2d7a47',
  },
  {
    level: 2,
    label: 'THREE QUESTIONS APPLIED',
    ar: 'ثَلَاثَة أَسْئِلَة',
    description: 'The deliberate application of the Three Questions — Is it? What is it? When is/was it? Layer 2 of the IRL Lens: Examination. Unlocks when you have completed at least one observation.',
    unlockCondition: 'Complete 1 observation',
    isUnlocked: false,
    color: '#2d8a87',
  },
  {
    level: 3,
    label: 'NAMES EXTRACTED',
    ar: 'أَسْمَاء',
    description: 'The Names of Allah active in this encounter — which divine attributes are manifesting here. Layer 3 of the IRL Lens: Names Fission. The fission process becomes available.',
    unlockCondition: 'Complete 3 observations',
    isUnlocked: false,
    color: '#c9a84c',
  },
  {
    level: 4,
    label: 'SUNNAT ALLAH MAPPED',
    ar: 'سُنَّة اللَّه',
    description: 'The process this thing participates in — each step a divine command executing. Layers 4 of the IRL Lens: Sunnat Allah. The order of operations becomes visible.',
    unlockCondition: 'Complete 5 observations',
    isUnlocked: false,
    color: '#8a8ab8',
  },
  {
    level: 5,
    label: 'ZAHIR · BATIN UNFOLDED',
    ar: 'ظَاهِر · بَاطِن',
    description: 'The dual axis — what is visible and what is hidden. Layers 5–6 of the IRL Lens: Zahir/Batin Depth. Az-Zahir and Al-Batin as paired Names. The IRL Lens descends from surface to depth.',
    unlockCondition: 'Complete 8 observations',
    isUnlocked: false,
    color: '#5a5a8a',
  },
  {
    level: 6,
    label: 'HAQQ DECLARED',
    ar: 'حَق',
    description: 'The truth of the matter — what is actually happening beneath the surface. Layer 7 of the IRL Lens: Haqq. The structural reality behind the visible sign. The recognition itself.',
    unlockCondition: 'Complete 12 observations',
    isUnlocked: false,
    color: '#c9a84c',
  },
  {
    level: 7,
    label: 'AMANAH FULFILLED + BRIDGE LINKS',
    ar: 'عُبُور',
    description: 'The crossing — from recognition to action, from seeing to doing. Layer 8 of the IRL Lens: Amanah + Bridges. The arc completes. الْحَمْدُ لِلَّه — every recognition returns to gratitude.',
    unlockCondition: 'Complete 20 observations',
    isUnlocked: false,
    color: '#c9a84c',
  },
];

const OBSERVATION_THRESHOLDS = [0, 1, 3, 5, 8, 12, 20];

// ── Sample Ayat for collaborative annotation ──────────────────────────────────
const AYAT_FOR_ANNOTATION = [
  { ref: '11:6', title: 'Provision Guaranteed', arabic: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا', nameOfAllah: 'Ar-Razzaq' },
  { ref: '57:3', title: 'Zahir and Batin', arabic: 'هُوَ الْأَوَّلُ وَالْآخِرُ وَالظَّاهِرُ وَالْبَاطِنُ', nameOfAllah: 'Az-Zahir · Al-Batin' },
  { ref: '59:24', title: 'Creator · Evolver · Fashioner', arabic: 'هُوَ اللَّهُ الْخَالِقُ الْبَارِئُ الْمُصَوِّرُ', nameOfAllah: 'Al-Khaliq · Al-Bari · Al-Musawwir' },
  { ref: '24:35', title: 'Light of the Heavens', arabic: 'اللَّهُ نُورُ السَّمَاوَاتِ وَالْأَرْضِ', nameOfAllah: 'An-Nur' },
  { ref: '4:135', title: 'Stand Firmly for Justice', arabic: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ', nameOfAllah: 'Al-Muqsit' },
];

// ── Primary vantage points (3 core + extended) ────────────────────────────────
const PRIMARY_VANTAGE_POINTS = [
  { id: 'ecological', label: 'Ecological', ar: 'بِيئِي', color: '#2d7a47', icon: '🌿', desc: 'Observing through the lens of creation, ecology, and natural signs' },
  { id: 'justice', label: 'Justice', ar: 'عَدَالَة', color: '#c9a84c', icon: '⚖️', desc: 'Observing through the lens of Al-Adl, Mizan, and structural reality' },
  { id: 'inner-seeker', label: 'Inner Seeker', ar: 'بَاطِن', color: '#8a8ab8', icon: '◎', desc: 'Observing through the lens of inner reality, nafs, and spiritual depth' },
];

const EXTENDED_VANTAGE_POINTS = [
  'Community Caretaker', 'Academic Researcher', 'Local Resident', 'Returning Citizen',
];

const ALL_VANTAGE_POINTS = [
  ...PRIMARY_VANTAGE_POINTS.map(v => v.label),
  ...EXTENDED_VANTAGE_POINTS,
];

const CITY_OPTIONS = ['Chicago', 'Atlanta', 'Portland', 'Detroit', 'Pittsburgh', 'Durham', 'Louisville', 'Cincinnati', 'Omaha'];

// ── Conflict detection: observations that contradict each other ───────────────
function detectConflicts(annotations: CollaborativeAnnotation[]): CollaborativeAnnotation[] {
  // Flag observations from the same Ayat with opposing zahir/batin interpretations
  // Simple heuristic: if 2+ observations exist and one says "visible" while another says "hidden" in zahir
  return annotations.map((ann, i) => {
    const others = annotations.filter((_, j) => j !== i);
    const hasConflict = others.some(other => {
      if (!ann.zahirNote || !other.zahirNote) return false;
      // Check for opposing keywords
      const conflictKeywords = [['visible', 'hidden'], ['present', 'absent'], ['clear', 'obscured'], ['open', 'closed']];
      return conflictKeywords.some(([a, b]) =>
        (ann.zahirNote.toLowerCase().includes(a) && other.zahirNote.toLowerCase().includes(b)) ||
        (ann.zahirNote.toLowerCase().includes(b) && other.zahirNote.toLowerCase().includes(a))
      );
    });
    return { ...ann, conflictFlag: hasConflict };
  });
}

// ── Collaborative Observation Mode ────────────────────────────────────────────
export function CollaborativeObservationMode() {
  const [activeAyat, setActiveAyat] = useState(0);
  const [annotations, setAnnotations] = useState<CollaborativeAnnotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedVantage, setSelectedVantage] = useState<string>(PRIMARY_VANTAGE_POINTS[0].id);
  const [filterVantage, setFilterVantage] = useState<string>('all');
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [form, setForm] = useState({
    observation: '',
    zahirNote: '',
    batinNote: '',
    vantagePoint: PRIMARY_VANTAGE_POINTS[0].label,
    authorName: '',
    city: CITY_OPTIONS[0],
    anonymous: false,
  });
  const realtimeRef = useRef<any>(null);

  const currentAyat = AYAT_FOR_ANNOTATION[activeAyat];
  const activeVantageInfo = PRIMARY_VANTAGE_POINTS.find(v => v.id === selectedVantage) || PRIMARY_VANTAGE_POINTS[0];

  useEffect(() => {
    fetchAnnotations();
    setupRealtime();
    return () => {
      if (realtimeRef.current) {
        realtimeRef.current.unsubscribe?.();
      }
    };
  }, [activeAyat]);

  async function setupRealtime() {
    try {
      const { supabase } = await import('@/lib/supabaseClient');
      if (realtimeRef.current) realtimeRef.current.unsubscribe?.();
      realtimeRef.current = supabase
        .channel('collaborative-observations')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'irl_observations' }, () => {
          fetchAnnotations();
        })
        .subscribe();
    } catch {
      // Realtime not available, polling fallback handled by submit
    }
  }

  async function fetchAnnotations() {
    setLoading(true);
    try {
      const { supabase } = await import('@/lib/supabaseClient');
      const { data } = await supabase
        .from('irl_observations')
        .select('id, title, city, zahir, batin, q_what, variant, created_at, selected_names')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(20);

      if (data && data.length > 0) {
        const mapped: CollaborativeAnnotation[] = data.map((row: {
          id: string; title: string; city: string; zahir: string; batin: string;
          q_what: string; variant: string; created_at: string; selected_names: string[];
        }) => ({
          id: row.id,
          ayatRef: currentAyat.ref,
          ayatTitle: row.title || currentAyat.title,
          vantagePoint: row.variant || 'Caretaker',
          observation: row.q_what || '',
          zahirNote: row.zahir || '',
          batinNote: row.batin || '',
          authorRole: 'caretaker',
          authorName: 'Anonymous Caretaker',
          city: row.city || 'Unknown',
          nameOfAllah: (row.selected_names || []).join(' · ') || currentAyat.nameOfAllah,
          createdAt: row.created_at,
          reactions: { alhamdulilah: 0, subhanallah: 0, inspiring: 0 },
        }));
        setAnnotations(detectConflicts(mapped));
      } else {
        setAnnotations([]);
      }
    } catch {
      setAnnotations([]);
    }
    setLoading(false);
  }

  async function handleSubmit() {
    if (!form.observation.trim()) return;
    setSubmitting(true);
    try {
      const { supabase } = await import('@/lib/supabaseClient');
      await supabase.from('irl_observations').insert([{
        title: `${currentAyat.title} — ${form.vantagePoint}`,
        city: form.city,
        q_what: form.observation,
        zahir: form.zahirNote,
        batin: form.batinNote,
        selected_names: [currentAyat.nameOfAllah],
        variant: form.vantagePoint,
        status: 'pending',
      }]);
      setSubmitted(true);
      setShowForm(false);
      setForm({ observation: '', zahirNote: '', batinNote: '', vantagePoint: activeVantageInfo.label, authorName: '', city: CITY_OPTIONS[0], anonymous: false });
      setTimeout(() => {
        setSubmitted(false);
        fetchAnnotations();
      }, 2000);
    } catch {
      setSubmitted(false);
    }
    setSubmitting(false);
  }

  // Update form vantage when selector changes
  function handleVantageSelect(vantageId: string) {
    setSelectedVantage(vantageId);
    const info = PRIMARY_VANTAGE_POINTS.find(v => v.id === vantageId);
    if (info) setForm(prev => ({ ...prev, vantagePoint: info.label }));
  }

  const filteredAnnotations = annotations.filter(ann => {
    if (showConflictsOnly && !ann.conflictFlag) return false;
    if (filterVantage !== 'all' && !ann.vantagePoint.toLowerCase().includes(filterVantage.toLowerCase())) return false;
    return true;
  });

  const conflictCount = annotations.filter(a => a.conflictFlag).length;

  return (
    <div style={{ background: 'rgba(6,13,18,0.95)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(45,138,135,0.15)', background: 'rgba(45,138,135,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.15em', textTransform: 'uppercase' }}>
            ◉ COLLABORATIVE OBSERVATION MODE
          </span>
          <span style={{ fontSize: 8, color: 'rgba(45,138,135,0.5)', padding: '1px 6px', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 3 }}>
            Multiple Vantage Points
          </span>
          {conflictCount > 0 && (
            <span style={{ fontSize: 8, color: '#c9a84c', padding: '1px 6px', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 3, background: 'rgba(201,168,76,0.06)' }}>
              ⚡ {conflictCount} tension{conflictCount > 1 ? 's' : ''} detected
            </span>
          )}
        </div>
        <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.5 }}>
          Multiple caretakers annotate the same Ayat from different vantage points — ecological observer, justice advocate, inner seeker, local resident. Each perspective reveals a different layer of the same sign.
        </p>
      </div>

      {/* ── PRIMARY VANTAGE POINT SELECTOR ── */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(45,138,135,0.1)', background: 'rgba(6,13,18,0.3)' }}>
        <div style={{ fontSize: 8, color: 'rgba(45,138,135,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
          Your Vantage Point — Select Before Annotating
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
          {PRIMARY_VANTAGE_POINTS.map(vp => (
            <button
              key={vp.id}
              onClick={() => handleVantageSelect(vp.id)}
              style={{
                padding: '10px 8px',
                background: selectedVantage === vp.id ? `${vp.color}12` : 'rgba(6,13,18,0.5)',
                border: `1px solid ${selectedVantage === vp.id ? vp.color : 'rgba(61,54,39,0.2)'}`,
                borderRadius: 4,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 16, marginBottom: 4 }}>{vp.icon}</div>
              <div style={{ fontSize: 9, color: selectedVantage === vp.id ? vp.color : '#7a6f5d', fontWeight: selectedVantage === vp.id ? 'bold' : 'normal', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 2 }}>
                {vp.label}
              </div>
              <div style={{ fontFamily: 'serif', fontSize: 11, color: selectedVantage === vp.id ? vp.color : 'rgba(201,168,76,0.3)' }}>
                {vp.ar}
              </div>
            </button>
          ))}
        </div>
        {/* Vantage description */}
        <div style={{ padding: '6px 10px', background: `${activeVantageInfo.color}06`, border: `1px solid ${activeVantageInfo.color}15`, borderRadius: 3 }}>
          <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.5 }}>{activeVantageInfo.desc}</p>
        </div>
        {/* Extended vantage options */}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Other Vantage Points</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {EXTENDED_VANTAGE_POINTS.map(vp => (
              <button
                key={vp}
                onClick={() => { setSelectedVantage(vp); setForm(prev => ({ ...prev, vantagePoint: vp })); }}
                style={{
                  padding: '3px 8px',
                  background: selectedVantage === vp ? 'rgba(45,138,135,0.1)' : 'transparent',
                  border: `1px solid ${selectedVantage === vp ? 'rgba(45,138,135,0.4)' : 'rgba(45,138,135,0.12)'}`,
                  borderRadius: 3,
                  color: selectedVantage === vp ? '#2d8a87' : '#7a6f5d',
                  fontSize: 8,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                {vp}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Ayat selector */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(45,138,135,0.1)' }}>
        <div style={{ fontSize: 8, color: 'rgba(45,138,135,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>Select Ayat to Annotate</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {AYAT_FOR_ANNOTATION.map((ayat, i) => (
            <button
              key={ayat.ref}
              onClick={() => setActiveAyat(i)}
              style={{
                padding: '4px 10px',
                background: activeAyat === i ? 'rgba(45,138,135,0.15)' : 'transparent',
                border: `1px solid ${activeAyat === i ? 'rgba(45,138,135,0.5)' : 'rgba(45,138,135,0.15)'}`,
                borderRadius: 3,
                color: activeAyat === i ? '#2d8a87' : '#7a6f5d',
                fontSize: 9,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s',
              }}
            >
              {ayat.ref} — {ayat.title}
            </button>
          ))}
        </div>
      </div>

      {/* Active Ayat display */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(45,138,135,0.1)', background: 'rgba(6,13,18,0.4)' }}>
        <div style={{ fontFamily: 'serif', fontSize: 18, color: '#c9a84c', direction: 'rtl', marginBottom: 8, lineHeight: 1.8 }}>
          {currentAyat.arabic}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.06em' }}>Quran {currentAyat.ref}</span>
          <span style={{ fontSize: 8, color: '#2d8a87', padding: '1px 6px', border: '1px solid rgba(45,138,135,0.3)', borderRadius: 3 }}>
            {currentAyat.nameOfAllah}
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ padding: '8px 20px', borderBottom: '1px solid rgba(45,138,135,0.08)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.08em', textTransform: 'uppercase' }}>Filter:</span>
        <button
          onClick={() => setFilterVantage('all')}
          style={{ padding: '2px 8px', background: filterVantage === 'all' ? 'rgba(45,138,135,0.12)' : 'transparent', border: `1px solid ${filterVantage === 'all' ? 'rgba(45,138,135,0.4)' : 'rgba(45,138,135,0.1)'}`, borderRadius: 3, color: filterVantage === 'all' ? '#2d8a87' : '#7a6f5d', fontSize: 8, cursor: 'pointer', fontFamily: 'inherit' }}
        >All</button>
        {PRIMARY_VANTAGE_POINTS.map(vp => (
          <button
            key={vp.id}
            onClick={() => setFilterVantage(filterVantage === vp.label ? 'all' : vp.label)}
            style={{ padding: '2px 8px', background: filterVantage === vp.label ? `${vp.color}12` : 'transparent', border: `1px solid ${filterVantage === vp.label ? vp.color : 'rgba(45,138,135,0.1)'}`, borderRadius: 3, color: filterVantage === vp.label ? vp.color : '#7a6f5d', fontSize: 8, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {vp.icon} {vp.label}
          </button>
        ))}
        {conflictCount > 0 && (
          <button
            onClick={() => setShowConflictsOnly(v => !v)}
            style={{ padding: '2px 8px', background: showConflictsOnly ? 'rgba(201,168,76,0.1)' : 'transparent', border: `1px solid ${showConflictsOnly ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)'}`, borderRadius: 3, color: showConflictsOnly ? '#c9a84c' : '#7a6f5d', fontSize: 8, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            ⚡ Tensions only
          </button>
        )}
      </div>

      {/* Annotations from Supabase */}
      <div style={{ padding: '12px 20px' }}>
        <div style={{ fontSize: 8, color: 'rgba(45,138,135,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
          Caretaker Observations — {loading ? '...' : filteredAnnotations.length} vantage point{filteredAnnotations.length !== 1 ? 's' : ''}
        </div>

        {loading ? (
          // Skeleton loaders
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ padding: '10px 12px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(45,138,135,0.08)', borderRadius: 4 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <SkeletonBlock height={14} width={80} />
                  <SkeletonBlock height={14} width={60} />
                </div>
                <SkeletonBlock height={12} width="90%" className="mb-2" />
                <SkeletonBlock height={12} width="70%" />
              </div>
            ))}
          </div>
        ) : filteredAnnotations.length === 0 ? (
          // Empty state
          <div style={{ padding: '24px 16px', background: 'rgba(45,138,135,0.03)', border: '1px solid rgba(45,138,135,0.1)', borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>◉</div>
            <div style={{ fontSize: 12, color: '#d4c5a9', marginBottom: 6, fontWeight: 'bold' }}>
              {showConflictsOnly ? 'No tensions detected in current observations.' : 'No observations yet for this Ayat.'}
            </div>
            <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.6, maxWidth: 280, margin: '0 auto' }}>
              {showConflictsOnly
                ? 'All current observations are in alignment. Tensions emerge when caretakers see the same Ayat differently from their vantage points.'
                : `Be the first to annotate from the ${activeVantageInfo.label} vantage point. Your observation adds a layer to the collective understanding.`
              }
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
            {filteredAnnotations.map((ann) => {
              const vantageColor = PRIMARY_VANTAGE_POINTS.find(v => ann.vantagePoint.toLowerCase().includes(v.id) || ann.vantagePoint.toLowerCase().includes(v.label.toLowerCase()))?.color || '#2d8a87';
              return (
                <div key={ann.id} style={{
                  padding: '10px 12px',
                  background: ann.conflictFlag ? 'rgba(201,168,76,0.04)' : 'rgba(10,25,41,0.4)',
                  border: `1px solid ${ann.conflictFlag ? 'rgba(201,168,76,0.2)' : 'rgba(45,138,135,0.12)'}`,
                  borderRadius: 4,
                  position: 'relative',
                }}>
                  {ann.conflictFlag && (
                    <div style={{ position: 'absolute', top: 6, right: 8, fontSize: 7, color: '#c9a84c', letterSpacing: '.06em' }}>
                      ⚡ TENSION
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span style={{ fontSize: 8, color: vantageColor, padding: '1px 5px', border: `1px solid ${vantageColor}30`, borderRadius: 2 }}>
                      {ann.vantagePoint}
                    </span>
                    <span style={{ fontSize: 8, color: '#7a6f5d' }}>{ann.city}</span>
                    <span style={{ fontSize: 8, color: 'rgba(201,168,76,0.4)', marginLeft: 'auto' }}>
                      {new Date(ann.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {ann.observation && (
                    <p style={{ fontSize: 11, color: '#d4c5a9', lineHeight: 1.6, marginBottom: 5, fontStyle: 'italic' }}>
                      &ldquo;{ann.observation}&rdquo;
                    </p>
                  )}
                  {(ann.zahirNote || ann.batinNote) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 5 }}>
                      {ann.zahirNote && (
                        <div style={{ padding: '4px 6px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 3 }}>
                          <div style={{ fontSize: 7, color: '#2d7a47', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 }}>Zahir</div>
                          <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)' }}>{ann.zahirNote}</div>
                        </div>
                      )}
                      {ann.batinNote && (
                        <div style={{ padding: '4px 6px', background: 'rgba(90,90,138,0.06)', border: '1px solid rgba(90,90,138,0.15)', borderRadius: 3 }}>
                          <div style={{ fontSize: 7, color: '#8a8ab8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 }}>Batin</div>
                          <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)' }}>{ann.batinNote}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add observation button */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              marginTop: 12,
              width: '100%',
              padding: '10px',
              background: `${activeVantageInfo.color}08`,
              border: `1px solid ${activeVantageInfo.color}30`,
              borderRadius: 4,
              color: activeVantageInfo.color,
              fontSize: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${activeVantageInfo.color}15`; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${activeVantageInfo.color}08`; }}
          >
            {activeVantageInfo.icon} Add {activeVantageInfo.label} Observation
          </button>
        )}

        {/* Observation form */}
        {showForm && (
          <div style={{ marginTop: 12, padding: '14px', background: 'rgba(6,13,18,0.6)', border: `1px solid ${activeVantageInfo.color}20`, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 8, color: activeVantageInfo.color, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                {activeVantageInfo.icon} {activeVantageInfo.label} Observation — {currentAyat.ref}
              </span>
            </div>

            {/* Vantage point + city */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Vantage Point</div>
                <select
                  value={form.vantagePoint}
                  onChange={(e) => setForm(prev => ({ ...prev, vantagePoint: e.target.value }))}
                  style={{ width: '100%', padding: '5px 8px', background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(45,138,135,0.25)', borderRadius: 3, color: '#d4c5a9', fontSize: 10, fontFamily: 'inherit' }}
                >
                  {ALL_VANTAGE_POINTS.map(vp => <option key={vp} value={vp}>{vp}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>City</div>
                <select
                  value={form.city}
                  onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                  style={{ width: '100%', padding: '5px 8px', background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(45,138,135,0.25)', borderRadius: 3, color: '#d4c5a9', fontSize: 10, fontFamily: 'inherit' }}
                >
                  {CITY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Observation */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>What do you observe in this Ayat from your vantage point?</div>
              <textarea
                value={form.observation}
                onChange={(e) => setForm(prev => ({ ...prev, observation: e.target.value }))}
                placeholder={`What does this Ayat reveal from the ${form.vantagePoint} perspective?`}
                rows={3}
                style={{ width: '100%', padding: '8px', background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(45,138,135,0.25)', borderRadius: 3, color: '#d4c5a9', fontSize: 11, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>

            {/* Per-caretaker Zahir / Batin depth fields */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                Zahir · Batin Depth — Your {form.vantagePoint} Reading
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 7, color: '#2d7a47', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    ظَاهِر Zahir (Visible from your vantage)
                  </div>
                  <textarea
                    value={form.zahirNote}
                    onChange={(e) => setForm(prev => ({ ...prev, zahirNote: e.target.value }))}
                    placeholder={`What is visible to the ${form.vantagePoint}...`}
                    rows={3}
                    style={{ width: '100%', padding: '6px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 3, color: '#d4c5a9', fontSize: 10, fontFamily: 'inherit', resize: 'none' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 7, color: '#8a8ab8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    بَاطِن Batin (Hidden from your vantage)
                  </div>
                  <textarea
                    value={form.batinNote}
                    onChange={(e) => setForm(prev => ({ ...prev, batinNote: e.target.value }))}
                    placeholder={`What is hidden beneath what the ${form.vantagePoint} sees...`}
                    rows={3}
                    style={{ width: '100%', padding: '6px', background: 'rgba(90,90,138,0.04)', border: '1px solid rgba(90,90,138,0.2)', borderRadius: 3, color: '#d4c5a9', fontSize: 10, fontFamily: 'inherit', resize: 'none' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.observation.trim()}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: submitted ? 'rgba(45,122,71,0.2)' : `${activeVantageInfo.color}15`,
                  border: `1px solid ${submitted ? 'rgba(45,122,71,0.5)' : `${activeVantageInfo.color}40`}`,
                  borderRadius: 4,
                  color: submitted ? '#2d7a47' : activeVantageInfo.color,
                  fontSize: 10,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  opacity: !form.observation.trim() ? 0.5 : 1,
                }}
              >
                {submitted ? '✓ Submitted — الحمد لله' : submitting ? 'Submitting...' : 'Submit Observation'}
              </button>
              <button
                onClick={() => setShowForm(false)}
                style={{ padding: '8px 12px', background: 'transparent', border: '1px solid rgba(61,54,39,0.3)', borderRadius: 4, color: '#7a6f5d', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Batin Depth Unlock Panel ──────────────────────────────────────────────────
export function BatinDepthUnlock() {
  const [observationCount, setObservationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeLayer, setActiveLayer] = useState<number | null>(null);

  useEffect(() => {
    async function fetchCount() {
      setLoading(true);
      try {
        const { supabase } = await import('@/lib/supabaseClient');
        const { count } = await supabase
          .from('irl_observations')
          .select('*', { count: 'exact', head: true });
        setObservationCount(count || 0);
      } catch {
        const local = JSON.parse(localStorage.getItem('haal_active_quests') || '[]');
        setObservationCount(local.length);
      }
      setLoading(false);
    }
    fetchCount();
  }, []);

  const layers = BATIN_DEPTH_LAYERS.map((layer, i) => ({
    ...layer,
    isUnlocked: observationCount >= OBSERVATION_THRESHOLDS[i],
  }));

  const currentLevel = layers.filter(l => l.isUnlocked).length;
  const nextThreshold = OBSERVATION_THRESHOLDS[currentLevel] || null;
  const progressToNext = nextThreshold ? Math.min(100, (observationCount / nextThreshold) * 100) : 100;

  return (
    <div style={{ background: 'rgba(6,13,18,0.95)', border: '1px solid rgba(90,90,138,0.2)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(90,90,138,0.15)', background: 'rgba(90,90,138,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 8, color: '#8a8ab8', letterSpacing: '.15em', textTransform: 'uppercase' }}>
            ◉ BATIN DEPTH UNLOCK
          </span>
          <span style={{ fontSize: 8, color: 'rgba(90,90,138,0.5)', padding: '1px 6px', border: '1px solid rgba(90,90,138,0.2)', borderRadius: 3 }}>
            Progressive Revelation
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.5 }}>
          Hidden layers of the IRL Lens are progressively revealed as caretakers complete more observations. Each layer descends deeper from the visible surface (Zahir) to the hidden reality (Batin).
        </p>
      </div>

      {/* Progress */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(90,90,138,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 8, color: 'rgba(90,90,138,0.6)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
            Depth Level {currentLevel} / {layers.length}
          </span>
          <span style={{ fontSize: 10, color: '#8a8ab8', fontWeight: 'bold' }}>
            {loading ? (
              <span style={{ display: 'inline-block', width: 30, height: 12, background: 'rgba(90,90,138,0.15)', borderRadius: 3, animation: 'pulse 1.5s ease-in-out infinite' }} />
            ) : `${observationCount} observations`}
          </span>
        </div>
        <div style={{ height: 4, background: 'rgba(90,90,138,0.15)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'linear-gradient(90deg, #8a8ab8, #c9a84c)', borderRadius: 2, width: `${progressToNext}%`, transition: 'width 0.6s ease' }} />
        </div>
        {nextThreshold && currentLevel < layers.length && (
          <div style={{ fontSize: 9, color: '#7a6f5d', marginTop: 4 }}>
            {loading ? '...' : `${nextThreshold - observationCount} more observations to unlock Layer ${currentLevel + 1}: ${layers[currentLevel]?.label}`}
          </div>
        )}
      </div>

      {/* Depth layers */}
      <div style={{ padding: '12px 20px' }}>
        {loading ? (
          // Skeleton loaders for layers
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[1, 2, 3, 4, 5, 6, 7].map(i => (
              <div key={i} style={{ padding: '10px 12px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(90,90,138,0.08)', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(90,90,138,0.08)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ flex: 1 }}>
                  <SkeletonBlock height={10} width="60%" className="mb-1" />
                  <SkeletonBlock height={8} width="40%" />
                </div>
              </div>
            ))}
          </div>
        ) : layers.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#d4c5a9', marginBottom: 4 }}>No depth layers available.</div>
            <div style={{ fontSize: 10, color: '#7a6f5d' }}>Begin making observations to unlock the Batin depth system.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {layers.map((layer) => (
              <div
                key={layer.level}
                onClick={() => layer.isUnlocked ? setActiveLayer(activeLayer === layer.level ? null : layer.level) : null}
                style={{
                  padding: '10px 12px',
                  background: layer.isUnlocked
                    ? activeLayer === layer.level ? `${layer.color}12` : 'rgba(10,25,41,0.3)'
                    : 'rgba(6,13,18,0.4)',
                  border: `1px solid ${layer.isUnlocked ? (activeLayer === layer.level ? `${layer.color}40` : `${layer.color}15`) : 'rgba(61,54,39,0.15)'}`,
                  borderRadius: 4,
                  cursor: layer.isUnlocked ? 'pointer' : 'default',
                  opacity: layer.isUnlocked ? 1 : 0.4,
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: layer.isUnlocked ? `${layer.color}15` : 'rgba(61,54,39,0.1)',
                    border: `1px solid ${layer.isUnlocked ? `${layer.color}40` : 'rgba(61,54,39,0.2)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {layer.isUnlocked ? (
                      <span style={{ fontSize: 9, color: layer.color }}>{layer.level}</span>
                    ) : (
                      <span style={{ fontSize: 9, color: '#7a6f5d' }}>🔒</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 10, color: layer.isUnlocked ? '#d4c5a9' : '#7a6f5d', fontWeight: layer.isUnlocked ? 'bold' : 'normal', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                        {layer.label}
                      </span>
                      <span style={{ fontFamily: 'serif', fontSize: 12, color: layer.isUnlocked ? layer.color : 'rgba(201,168,76,0.2)' }}>
                        {layer.ar}
                      </span>
                    </div>
                    {!layer.isUnlocked && (
                      <div style={{ fontSize: 8, color: '#7a6f5d', marginTop: 1 }}>{layer.unlockCondition}</div>
                    )}
                  </div>
                  {layer.isUnlocked && (
                    <span style={{ fontSize: 10, color: layer.color, flexShrink: 0 }}>
                      {activeLayer === layer.level ? '↑' : '↓'}
                    </span>
                  )}
                </div>

                {/* Expanded content */}
                {layer.isUnlocked && activeLayer === layer.level && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${layer.color}20` }}>
                    <p style={{ fontSize: 11, color: '#d4c5a9', lineHeight: 1.6, marginBottom: 8 }}>{layer.description}</p>
                    {layer.level === 4 && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Link
                          href="/names-reference?name=zahir"
                          style={{ padding: '4px 10px', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 3, color: '#2d7a47', fontSize: 9, textDecoration: 'none', letterSpacing: '.06em' }}
                        >
                          Az-Zahir →
                        </Link>
                        <Link
                          href="/names-reference?name=batin"
                          style={{ padding: '4px 10px', background: 'rgba(90,90,138,0.1)', border: '1px solid rgba(90,90,138,0.3)', borderRadius: 3, color: '#8a8ab8', fontSize: 9, textDecoration: 'none', letterSpacing: '.06em' }}
                        >
                          Al-Batin →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
